import { APP_MODULES } from 'constants/appModules';
import { MODULE_ROOT_PATHS, PATHS } from 'constants/paths';
import { getPathParamName } from 'helpers/PathHelper';
import { useIsDev } from 'hooks/useIsDev';
import { useIsDevPO } from 'hooks/useItsDevPo';
import { useMemo } from 'react';
import { matchPath } from 'react-router-dom';
export type SidebarRoute = {
  path: string;
  name: string;
  module: APP_MODULES;
  paramName?: string;
  queryParam?: string;
  rootPath?: string;
  isDisabled?: boolean;
  isHidden?: boolean;
};
type SidebarRoutesGroup = {
  name: string;
  isHidden?: boolean;
  routes: SidebarRoute[];
};
export const isRouteActive = (
  pathname: string,
  { rootPath, path }: { rootPath?: string; path: string },
) => !!matchPath(pathname, { path: rootPath ?? path });
export const useSidebarRoutes = ({
  tuwi,
  wellboreId,
  tuwbi,
  tightWellAccessible,
  currentWell,
}: {
  tuwi?: string;
  wellboreId?: string;
  tuwbi?: string;
  tightWellAccessible: boolean;
  currentWell: any;
}): SidebarRoutesGroup[] => {
  const isDev = useIsDev();
  const isDevPO = useIsDevPO();

  return useMemo(() => {
    return [
      {
        name: 'sidebar.routes.reporting',
        routes: [
          {
            path: PATHS.EDDR_OVERVIEW,
            module: APP_MODULES.EDDR,
            paramName: getPathParamName(PATHS.EDDR_OVERVIEW),
            name: `sidebar.routes.modules.${APP_MODULES.EDDR}`,
            rootPath: MODULE_ROOT_PATHS.EDDR,
            isDisabled: !tuwi || (currentWell && currentWell.tightWell && !tightWellAccessible),
          },
          {
            path: PATHS.LESSONS_LEARNT_REGISTER,
            module: APP_MODULES.LESSONS_LEARNT,
            name: `sidebar.routes.modules.${APP_MODULES.LESSONS_LEARNT}`,
          },
        ],
      },
      {
        name: 'sidebar.routes.operations',
        routes: [
          {
            path: PATHS.KICK_PREDICTION,
            module: APP_MODULES.KICK_PREDICTION,
            paramName: getPathParamName(PATHS.KICK_PREDICTION),
            name: `sidebar.routes.modules.${APP_MODULES.KICK_PREDICTION}`,
            isDisabled: !wellboreId,
          },
          {
            path: PATHS.STUCK_PIPE_PREDICTION,
            module: APP_MODULES.STUCK_PIPE_PREDICTION,
            paramName: getPathParamName(PATHS.STUCK_PIPE_PREDICTION),
            name: `sidebar.routes.modules.${APP_MODULES.STUCK_PIPE_PREDICTION}`,
            isDisabled:
              !wellboreId && !(currentWell && currentWell.tightWell && !tightWellAccessible),
          },
          {
            path: PATHS.LIVE_PERFORMANCE,
            module: APP_MODULES.LIVE_PERFORMANCE,
            paramName: getPathParamName(PATHS.LIVE_PERFORMANCE),
            name: `sidebar.routes.modules.${APP_MODULES.LIVE_PERFORMANCE}`,
            isDisabled: !wellboreId,
          },
          {
            path: PATHS.ANTICOLLISION,
            module: APP_MODULES.ANTICOLLISION,
            paramName: getPathParamName(PATHS.ANTICOLLISION),
            name: `sidebar.routes.modules.${APP_MODULES.ANTICOLLISION}`,
            isDisabled: !wellboreId,
          },
          {
            path: PATHS.GHOST_MODE,
            module: APP_MODULES.GHOST_MODE,
            paramName: getPathParamName(PATHS.GHOST_MODE),
            name: `sidebar.routes.modules.${APP_MODULES.GHOST_MODE}`,
            isDisabled: !tuwbi,
          },
        ],
      },
      {
        name: 'sidebar.routes.performance',
        isHidden: !isDev,
        routes: [
          {
            path: PATHS.PLANNING_NEW_PLAN,
            module: APP_MODULES.PLANNING_PANEL,
            name: `sidebar.routes.modules.${APP_MODULES.PLANNING_PANEL}`,
            rootPath: MODULE_ROOT_PATHS.PLANNING_PANEL,
          },
          {
            path: PATHS.PLANNING_CATALOGUE,
            module: APP_MODULES.PLANNING_CATALOGUE,
            name: `sidebar.routes.modules.${APP_MODULES.PLANNING_CATALOGUE}`,
          },
          {
            path: PATHS.GLOBAL_BENCHMARK,
            module: APP_MODULES.GLOBAL_BENCHMARK,
            name: `sidebar.routes.modules.${APP_MODULES.GLOBAL_BENCHMARK}`,
            rootPath: MODULE_ROOT_PATHS.GLOBAL_BENCHMARK,
          },
          {
            queryParam: 'wellTuwis[]',
            module: APP_MODULES.PROGRESS_FOLLOW_UP,
            path: PATHS.PROGRESS_FOLLOW_UP,
            paramName: 'tuwi',
            name: `sidebar.routes.modules.${APP_MODULES.PROGRESS_FOLLOW_UP}`,
          },
        ],
      },
      {
        name: 'sidebar.routes.beta',
        isHidden: !isDevPO,
        routes: [
          {
            path: PATHS.KICK_DETECTION,
            module: APP_MODULES.KICK_DETECTION,
            paramName: getPathParamName(PATHS.KICK_DETECTION),
            name: `sidebar.routes.modules.${APP_MODULES.KICK_DETECTION}`,
            isDisabled: !isDevPO || !wellboreId,
          },
        ],
      },
    ];
  }, [tuwi, wellboreId, tuwbi, isDev, currentWell, tightWellAccessible, isDevPO]);
};
